// #780: client-side search/filter/sort/pagination for the MSP portal.
// ponytail: all client-side over the fetched set — tier max_children caps at 200
// (= the server list cap), so an MSP's full child/health set always fits one
// fetch. Add server-side paging only if a tier ever allows >200 children.
import type {
  MspTenant,
  MspAlertSummary,
  MspIntegrationHealth,
} from "@/types/api";

// ── Tenant overview ───────────────────────────────────────
export type TenantSort =
  | "name-asc"
  | "name-desc"
  | "alerts-desc"
  | "alerts-asc"
  | "status";

export interface TenantFilters {
  search: string;
  status: string; // "all" | a status value
  plan: string; // "all" | a plan value
}

// Fixed status order: active first, suspended next, anything else last.
const STATUS_ORDER: Record<string, number> = { active: 0, suspended: 1 };
const statusRank = (s: string): number =>
  s in STATUS_ORDER ? STATUS_ORDER[s] : 2;

const nameCmp = (a: MspTenant, b: MspTenant): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) ||
  a.id.localeCompare(b.id);

/** Sum of severities per tenant_id, for the alerts sort/card counts. */
export function alertTotalsById(
  summary: MspAlertSummary[],
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const s of summary) {
    totals[s.tenant_id] = s.critical + s.high + s.medium + s.low;
  }
  return totals;
}

export function filterTenants(
  tenants: MspTenant[],
  f: TenantFilters,
): MspTenant[] {
  const q = f.search.trim().toLowerCase();
  return tenants.filter((t) => {
    if (
      q &&
      !t.name.toLowerCase().includes(q) &&
      !t.slug.toLowerCase().includes(q)
    ) {
      return false;
    }
    if (f.status !== "all" && t.status !== f.status) return false;
    if (f.plan !== "all" && t.plan !== f.plan) return false;
    return true;
  });
}

export function sortTenants(
  tenants: MspTenant[],
  sort: TenantSort,
  totals: Record<string, number>,
): MspTenant[] {
  const arr = [...tenants];
  switch (sort) {
    case "name-asc":
      arr.sort(nameCmp);
      break;
    case "name-desc":
      arr.sort((a, b) => -nameCmp(a, b));
      break;
    case "alerts-desc":
      arr.sort(
        (a, b) => (totals[b.id] ?? 0) - (totals[a.id] ?? 0) || nameCmp(a, b),
      );
      break;
    case "alerts-asc":
      arr.sort(
        (a, b) => (totals[a.id] ?? 0) - (totals[b.id] ?? 0) || nameCmp(a, b),
      );
      break;
    case "status":
      arr.sort(
        (a, b) => statusRank(a.status) - statusRank(b.status) || nameCmp(a, b),
      );
      break;
  }
  return arr;
}

/** Distinct non-empty plan values present, for building the plan filter. */
export function distinctPlans(tenants: MspTenant[]): string[] {
  return [...new Set(tenants.map((t) => t.plan).filter(Boolean))].sort();
}

// ── Integration health ────────────────────────────────────
export type HealthCol =
  | "tenant_name"
  | "source_type"
  | "health_status"
  | "consecutive_failures"
  | "last_poll_at";
export type SortDir = "asc" | "desc";

// "Needs attention" first.
const HEALTH_ORDER: Record<string, number> = {
  unhealthy: 0,
  unknown: 1,
  disabled: 2,
  healthy: 3,
};

const healthTie = (
  a: MspIntegrationHealth,
  b: MspIntegrationHealth,
): number =>
  a.tenant_name.localeCompare(b.tenant_name, undefined, {
    sensitivity: "base",
  }) || a.integration_id.localeCompare(b.integration_id);

export function filterSortHealth(
  rows: MspIntegrationHealth[],
  search: string,
  col: HealthCol,
  dir: SortDir,
): MspIntegrationHealth[] {
  const q = search.trim().toLowerCase();
  const out = q
    ? rows.filter(
        (r) =>
          r.tenant_name.toLowerCase().includes(q) ||
          r.source_type.toLowerCase().includes(q) ||
          // display_name is nullable at the DB (no COALESCE) despite the type.
          (r.display_name ?? "").toLowerCase().includes(q),
      )
    : [...rows];
  const sign = dir === "asc" ? 1 : -1;
  out.sort((a, b) => {
    let c = 0;
    switch (col) {
      case "tenant_name":
        c = a.tenant_name.localeCompare(b.tenant_name, undefined, {
          sensitivity: "base",
        });
        break;
      case "source_type":
        c = a.source_type.localeCompare(b.source_type, undefined, {
          sensitivity: "base",
        });
        break;
      case "health_status":
        c = (HEALTH_ORDER[a.health_status] ?? 99) -
          (HEALTH_ORDER[b.health_status] ?? 99);
        break;
      case "consecutive_failures":
        c = a.consecutive_failures - b.consecutive_failures;
        break;
      case "last_poll_at": {
        const an = a.last_poll_at;
        const bn = b.last_poll_at;
        // Nulls always sort last, regardless of direction.
        if (an === null || bn === null) {
          if (an === bn) break; // both null → tie-break below
          return an === null ? 1 : -1;
        }
        c = an.localeCompare(bn);
        break;
      }
    }
    if (c === 0) return healthTie(a, b);
    return c * sign;
  });
  return out;
}

// ── Pagination ────────────────────────────────────────────
export interface Page<T> {
  pageItems: T[];
  totalPages: number;
  safePage: number;
}

export function paginate<T>(items: T[], page: number, size: number): Page<T> {
  const totalPages = Math.max(1, Math.ceil(items.length / size));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * size;
  return { pageItems: items.slice(start, start + size), totalPages, safePage };
}
